import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShoppingBag, Mail } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import api from "../store/api";
import { useCart } from "../store/CartContext";
import { useSiteText } from "../store/useSiteContent";
import CmsText from "../components/CmsText";

const CATEGORIES = [
  { v: "all", label: "All" },
  { v: "boxes", label: "Boxes" },
  { v: "tubers", label: "Tubers" },
  { v: "workshops", label: "Workshops" },
  { v: "apparel", label: "Apparel" },
];

function priceRange(p) {
  const prices = p.variants && p.variants.length
    ? p.variants.map((v) => p.base_price + (v.price_delta || 0))
    : [p.base_price];
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `NZ$${min.toFixed(2)}`;
  return `NZ$${min.toFixed(2)} to ${max.toFixed(2)}`;
}

function totalStock(p) {
  if (!p.variants || !p.variants.length) return null;
  return p.variants.reduce((a, v) => a + (v.stock || 0), 0);
}

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const { totalQty } = useCart();
  const eyebrow = useSiteText("shop.eyebrow", "✦ THE FARM SHOP ✦");
  const heading = useSiteText("shop.heading", "Shop *Heritage.*");
  const emptyHeading = useSiteText("shop.empty.heading", "The shop is *resting.*");
  const emptyBody = useSiteText(
    "shop.empty.body",
    "Tubers, workshops and a few small things will land here when the season turns. Send us a note and we'll let you know the day they go up."
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/products").then((r) => {
      if (alive) setProducts(r.data || []);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const visible = filter === "all" ? products : products.filter((p) => p.category === filter);
  const isEmptyShop = !loading && products.length === 0;

  return (
    <DahliasShell>
      <section data-testid="shop-page" className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="font-display text-[var(--dahlias-clay)] tracking-[0.35em] text-sm">{eyebrow}</span>
            <CmsText
              as="h1"
              text={heading}
              className="font-display text-4xl md:text-6xl text-[var(--dahlias-forest)] mt-3 leading-[1.05]"
            />
          </div>
          {!isEmptyShop && (
            <Link
              to="/cart"
              data-testid="shop-cart-link"
              className="inline-flex items-center gap-2 px-5 py-3 border border-[var(--dahlias-ink)]/15 hover:border-[var(--dahlias-clay)] transition rounded-full"
            >
              <ShoppingBag className="w-4 h-4" />
              Cart{totalQty ? ` (${totalQty})` : ""}
            </Link>
          )}
        </div>

        {loading && (
          <p className="mt-12 text-center text-[var(--dahlias-ink)]/60">Loading the shop…</p>
        )}

        {/* Empty / pre-launch state */}
        {isEmptyShop && (
          <div data-testid="shop-coming-soon" className="mt-14 md:mt-20">
            <div className="max-w-2xl mx-auto text-center bg-[var(--dahlias-paper)] border border-[var(--dahlias-ink)]/10 px-6 md:px-12 py-14 md:py-20">
              <span className="inline-flex items-center gap-2 px-5 py-2 font-label tracking-[0.4em] text-xs md:text-sm bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] rounded-full uppercase">
                ✦ Coming Soon ✦
              </span>
              <CmsText
                as="h2"
                text={emptyHeading}
                italicClass="font-display-italic text-[var(--dahlias-sage-deep)]"
                className="font-display text-3xl md:text-5xl text-[var(--dahlias-forest)] mt-7 leading-[1.05]"
              />
              <p className="mt-5 text-base md:text-lg text-[var(--dahlias-ink)]/75 leading-relaxed whitespace-pre-line">
                {emptyBody}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/dahlias/about"
                  data-testid="shop-coming-soon-contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] rounded-full font-medium tracking-wide shadow-md transition-colors"
                >
                  <Mail className="w-4 h-4" /> Get in touch
                </Link>
                <Link
                  to="/dahlias"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-sage-deep)] hover:bg-[var(--dahlias-sage)]/10 rounded-full text-[var(--dahlias-ink)]/80 transition"
                >
                  Back to the farm
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Real catalogue */}
        {!loading && products.length > 0 && (
          <>
            {/* Category filter */}
            <div className="mt-8 flex flex-wrap gap-2" data-testid="shop-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setFilter(c.v)}
                  data-testid={`shop-filter-${c.v}`}
                  className={`px-5 py-2 text-base font-medium border rounded-full transition-colors ${
                    filter === c.v
                      ? "bg-[var(--dahlias-forest)] text-[var(--dahlias-cream)] border-[var(--dahlias-forest)]"
                      : "bg-[var(--dahlias-paper)] text-[var(--dahlias-ink)] border-[var(--dahlias-ink)]/15 hover:border-[var(--dahlias-clay)]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {visible.map((p) => {
                const stock = totalStock(p);
                return (
                  <Link
                    key={p.id}
                    to={`/shop/${p.slug}`}
                    data-testid={`product-card-${p.slug}`}
                    className="group bg-[var(--dahlias-paper)] border border-[var(--dahlias-ink)]/10 hover:border-[var(--dahlias-clay)] transition-colors overflow-hidden flex flex-col"
                  >
                    <div className="aspect-[4/3] bg-[var(--dahlias-cream)] bg-cover bg-center" style={{ backgroundImage: `url(${p.image_url || "/photos/dahlia-field.jpg"})` }} />
                    <div className="p-5 md:p-6 flex flex-col flex-1">
                      <span className="font-display text-[var(--dahlias-clay)] tracking-[0.3em] text-xs uppercase">{p.category}</span>
                      <h3 className="font-display text-xl md:text-2xl text-[var(--dahlias-forest)] mt-2 leading-tight">{p.name}</h3>
                      <p className="mt-2 text-sm text-[var(--dahlias-ink)]/75 leading-relaxed flex-1">{p.blurb}</p>
                      <div className="mt-5 flex items-end justify-between">
                        <span className="font-display text-xl text-[var(--dahlias-clay)]">{priceRange(p)}</span>
                        <span className="text-xs text-[var(--dahlias-ink)]/55">
                          {stock === null ? "" : stock === 0 ? "Sold out" : `${stock} in stock`}
                        </span>
                      </div>
                      <span className="mt-4 inline-flex items-center gap-1 text-[var(--dahlias-forest)] font-medium group-hover:text-[var(--dahlias-clay)] transition">
                        View <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {visible.length === 0 && (
              <p className="mt-12 text-center text-[var(--dahlias-ink)]/60">Nothing in this section yet.</p>
            )}
          </>
        )}
      </section>
    </DahliasShell>
  );
}
